// Import Dependencies
import {
  UserPlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserIcon,
  CogIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BellAlertIcon,
  PaperAirplaneIcon,
  VideoCameraIcon,
  PencilSquareIcon,
  ShareIcon,
  FlagIcon,
  XCircleIcon,
  InboxIcon,
} from "@heroicons/react/24/outline";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";

// Third-party Imports
import InfiniteScroll from "react-infinite-scroll-component";

// Local Imports
import { Timeline, Tag, Button, TimelineItem } from "components/ui";
import { Card } from "components/ui";
import { useTimeline } from "hooks/useTimeline";
import { formatDisplayDate } from "../../../utils/dateUtils";
import { useState, useEffect, useCallback, useRef } from "react";
import { getJobByLeadId } from "../../../services/jobsService";
import { parseClientNoteDetails } from "../../../utils/activityFormatters.js";
import {
  FormattedHtmlContent,
  ClientNoteDetailsView,
} from "../../../utils/activityFormatters.jsx";


// ----------------------------------------------------------------------

function LeadTimeline({ leadId = null, jobId = null, entityName = "Lead", scrollTargetId = null, refreshTrigger = 0, isActive = true }) {
  const PAGE_SIZE = 20; // Number of activities per API call

  const [resolvedJobId, setResolvedJobId] = useState(jobId);
  const [isResolvingJob, setIsResolvingJob] = useState(!jobId && leadId);

  useEffect(() => {
    if (jobId) {
      setResolvedJobId(jobId);
      setIsResolvingJob(false);
      return;
    }
    
    if (leadId) {
      setIsResolvingJob(true);
      getJobByLeadId(leadId).then((res) => {
        if (res.success && res.data) {
          let jobData = res.data?.data || res.data;
          if (typeof jobData === "string") {
            try {
              jobData = JSON.parse(jobData);
            } catch (e) {
              /* ignore parse error */
            }
          }
          if (Array.isArray(jobData) && jobData.length > 0) jobData = jobData[0];
          const jobObj = jobData?.Job || jobData;
          if (jobObj?.JobId) {
            setResolvedJobId(jobObj.JobId);
          }
        }
      }).catch(err => {
        console.error("Error fetching job for timeline:", err);
      }).finally(() => {
        setIsResolvingJob(false);
      });
    } else {
      setIsResolvingJob(false);
    }
  }, [leadId, jobId]);

  const {
    activities,
    totalRecords,
    isLoading,
    isValidating,
    hasMore,
    error,
    loadMore: loadMoreData,
    refresh: handleRefresh,
  } = useTimeline(isResolvingJob ? null : leadId, PAGE_SIZE, isResolvingJob ? null : resolvedJobId);

  // Auto-refresh when tab becomes active or refreshTrigger changes
  useEffect(() => {
    if (isActive && !isResolvingJob) {
      handleRefresh();
    }
  }, [isActive, refreshTrigger, handleRefresh, isResolvingJob]);

  const loading = isLoading || isValidating || isResolvingJob;

  // Refs
  const timelineScrollElementId = `timeline-scroll-${leadId}`;
  const scrollRef = useRef(null);

  const handleLoadMore = useCallback(() => {
    if (scrollRef.current && scrollRef.current.offsetParent === null) {
      return;
    }
    loadMoreData();
  }, [loadMoreData]);

  // Function to get activity icon and color based on type and details
  const getActivityStyle = (activityType, details = "") => {
    // Handle undefined or null activityType
    if (!activityType) {
      return { icon: ClockIcon, color: "neutral", pointClass: "this:neutral" };
    }

    let type = activityType.toLowerCase();
    const detailsLower = (details || "").toLowerCase();

    // If generic "Activity", try to infer type from details for better styling
    if (type === "activity") {
      if (
        detailsLower.includes("follow-up") ||
        detailsLower.includes("follow up")
      ) {
        type = "follow-up";
      } else if (
        detailsLower.includes("call") ||
        detailsLower.includes("phone")
      ) {
        type = "call";
      } else if (
        detailsLower.includes("meeting") ||
        detailsLower.includes("schedule")
      ) {
        type = "meeting";
      } else if (detailsLower.includes("email") || detailsLower.includes("mail")) {
        type = "email";
      }
    }

    // Stage Change activities with specific icons based on details
    if (type.includes("stage")) {
      if (detailsLower.includes("interested")) {
        return {
          icon: CheckCircleIcon,
          color: "success",
          pointClass: "this:success",
        };
      }
      if (
        detailsLower.includes("enquiry") ||
        detailsLower.includes("inquiry")
      ) {
        return {
          icon: InformationCircleIcon,
          color: "info",
          pointClass: "this:info",
        };
      }
      if (detailsLower.includes("closed") || detailsLower.includes("won")) {
        return { icon: FlagIcon, color: "success", pointClass: "this:success" };
      }
      if (detailsLower.includes("lost") || detailsLower.includes("rejected")) {
        return { icon: XCircleIcon, color: "error", pointClass: "this:error" };
      }
      if (detailsLower.includes("proposal") || detailsLower.includes("quote")) {
        return {
          icon: DocumentTextIcon,
          color: "warning",
          pointClass: "this:warning",
        };
      }
      if (
        detailsLower.includes("negotiation") ||
        detailsLower.includes("discussion")
      ) {
        return {
          icon: ChatBubbleLeftRightIcon,
          color: "warning",
          pointClass: "this:warning",
        };
      }
      // Default stage change
      return { icon: ArrowPathIcon, color: "info", pointClass: "this:info" };
    }

    // Communication activities
    if (type.includes("email") || type.includes("mail")) {
      return {
        icon: EnvelopeIcon,
        color: "primary",
        pointClass: "this:primary",
      };
    }

    if (type.includes("call") || type.includes("phone")) {
      return {
        icon: PhoneIcon,
        color: "secondary",
        pointClass: "this:secondary",
      };
    }

    if (
      type.includes("message") ||
      type.includes("sms") ||
      type.includes("text")
    ) {
      return {
        icon: PaperAirplaneIcon,
        color: "info",
        pointClass: "this:info",
      };
    }

    if (type.includes("chat") || type.includes("whatsapp")) {
      return {
        icon: ChatBubbleLeftRightIcon,
        color: "success",
        pointClass: "this:success",
      };
    }

    if (
      type.includes("video") ||
      type.includes("meeting") ||
      type.includes("demo")
    ) {
      return {
        icon: VideoCameraIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    // Document and task activities
    if (
      type.includes("document") ||
      type.includes("file") ||
      type.includes("upload")
    ) {
      return {
        icon: DocumentArrowDownIcon,
        color: "info",
        pointClass: "this:info",
      };
    }

    // Client note activities
    if (
      type.includes("client note") ||
      detailsLower.includes("client note") ||
      detailsLower.includes("client wish")
    ) {
      return {
        icon: PencilSquareIcon,
        color: "primary",
        pointClass: "this:primary",
      };
    }

    if (
      type.includes("note") ||
      type.includes("comment") ||
      type.includes("remark")
    ) {
      return {
        icon: PencilSquareIcon,
        color: "neutral",
        pointClass: "this:neutral",
      };
    }

    if (type.includes("task") || type.includes("assignment")) {
      return {
        icon: CheckCircleIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    if (type.includes("follow") || type.includes("reminder")) {
      return {
        icon: BellAlertIcon,
        color: "warning",
        pointClass: "this:warning",
      };
    }

    if (type.includes("share") || type.includes("forward")) {
      return { icon: ShareIcon, color: "info", pointClass: "this:info" };
    }

    // System activities
    if (
      type.includes("created") ||
      type.includes("added") ||
      type.includes("new")
    ) {
      return {
        icon: UserPlusIcon,
        color: "success",
        pointClass: "this:success",
      };
    }

    if (
      type.includes("updated") ||
      type.includes("modified") ||
      type.includes("changed")
    ) {
      return { icon: CogIcon, color: "info", pointClass: "this:info" };
    }

    if (type.includes("deleted") || type.includes("removed")) {
      return { icon: XCircleIcon, color: "error", pointClass: "this:error" };
    }

    if (
      type.includes("alert") ||
      type.includes("warning") ||
      type.includes("issue")
    ) {
      return {
        icon: ExclamationTriangleIcon,
        color: "error",
        pointClass: "this:error",
      };
    }

    if (
      type.includes("schedule") ||
      type.includes("calendar") ||
      type.includes("appointment")
    ) {
      return { icon: CalendarDaysIcon, color: "info", pointClass: "this:info" };
    }

    // Default fallback
    return { icon: ClockIcon, color: "neutral", pointClass: "this:neutral" };
  };

  // Helper function to infer proper title from activity
  const getActivityTitle = (activity) => {
    let type = activity.ActivityType || "Activity";
    const typeUpper = type.toUpperCase();
    const detailsLower = (activity.Details || "").toLowerCase();

    // Human-readable titles for follow-up event codes
    const followupTitleMap = {
      FOLLOWUP_UPDATED: "Follow-up Updated",
      FOLLOWUP_COMPLETED: "Follow-up Completed",
      FOLLOWUP_SCHEDULED: "Follow-up Scheduled",
      FOLLOWUP_RESCHEDULED: "Follow-up Rescheduled",
      FOLLOWUP_ASSIGNEE: "Follow-up Reassigned",
      FOLLOWUP_REASSIGNED: "Follow-up Reassigned",
      FOLLOWUP_DELETED: "Follow-up Deleted",
      FOLLOWUP_CANCELLED: "Follow-up Cancelled",
      FOLLOWUP_PENDING: "Follow-up Pending",
      FOLLOWUP_MISSED: "Follow-up Missed",
    };

    if (followupTitleMap[typeUpper]) {
      return followupTitleMap[typeUpper];
    }

    if (typeUpper.startsWith("FOLLOWUP_")) {
      const suffix = typeUpper
        .replace("FOLLOWUP_", "")
        .toLowerCase()
        .replace(/_/g, " ");
      return `Follow-up ${suffix.charAt(0).toUpperCase() + suffix.slice(1)}`;
    }

    // If generic "Activity", try to infer type from details
    if (type === "Activity") {
      if (detailsLower.includes("client note updated")) return "Client Note Updated";
      if (detailsLower.includes("client note added")) return "Client Note Added";
      if (detailsLower.includes("client note deleted")) return "Client Note Deleted";
      if (detailsLower.includes("client wish")) return "Client Note Updated";
      if (
        detailsLower.includes("follow-up") ||
        detailsLower.includes("follow up")
      ) {
        return "Follow-up Activity";
      }
      if (detailsLower.includes("call") || detailsLower.includes("phone")) {
        return "Call Activity";
      }
      if (
        detailsLower.includes("meeting") ||
        detailsLower.includes("schedule")
      ) {
        return "Scheduled Event";
      }
      if (detailsLower.includes("email")) {
        return "Email Activity";
      }
    }

    // Format any uppercase snake_case event code into Title Case (e.g. STAGE_CHANGED -> Stage Changed)
    if (/^[A-Z0-9_]+$/.test(type) && type.includes("_")) {
      return type
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }

    return type;
  };



  // Function to extract useful info from activity details
  const formatActivityDetails = (details) => {
    if (!details) return { main: "No details available", remarks: null };

    // Common pattern in details: "Some action. Remarks: some remarks"
    const parts = details.split(/Remarks:|Note:/i);
    return {
      main: parts[0].trim(),
      remarks: parts.length > 1 ? parts[1].trim() : null,
    };
  };

  // Helper to parse diffs from activity details.
  // Two shapes are understood: a bare [{field,old,new}, ...] array (what Leads and Job-detail
  // edits produce, where the activity's own context already says what it's about), and
  // {"subject": "...", "changes": [{field,old,new}, ...]} (schedule-task edits — a Job's Timeline
  // mixes every one of its tasks' edits together, so "subject" is the task name to show in the
  // title; without it a date/assignee-only change gives no clue which task it was).
  const parseDiffDetails = (details) => {
    if (!details || typeof details !== "string") return null;
    const trimmed = details.trim();

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.field !== undefined) {
          return { subject: null, fields: parsed };
        }
      } catch (e) {
        return null;
      }
      return null;
    }

    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed?.changes) && parsed.changes.length > 0 && parsed.changes[0]?.field !== undefined) {
          return { subject: parsed.subject || null, fields: parsed.changes };
        }
      } catch (e) {
        return null;
      }
    }

    return null;
  };

  // Helper to get stage change tag
  const getStageTag = (details, type) => {
    if (!type?.toLowerCase().includes("stage")) return null;

    // Try to extract stage names if available in details
    if (details?.includes(" to ")) {
      const toIndex = details.indexOf(" to ");
      const toStagePart = details.substring(toIndex + 4);
      const toStage = toStagePart.split(".")[0]?.trim();
      if (toStage) {
        return { label: toStage, color: "info" };
      }
    }

    return null;
  };

  // Refresh timeline when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      handleRefresh();
    }
  }, [refreshTrigger, handleRefresh]);

  return (
    <Card className="flex h-full flex-col border-none bg-transparent shadow-none overflow-hidden">
      {/* Fixed Header */}
      <div className="px-6 py-4 flex items-center justify-between bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm border-b border-gray-100 dark:border-dark-800 shrink-0">
        <h2 className="dark:text-dark-100 truncate font-medium tracking-wide text-gray-800">
          Timeline
          {totalRecords > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({totalRecords} activities)
            </span>
          )}
          {/* Loading indicator in header */}
          {loading && (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium">
              <div className="border-blue-500 h-2 w-2 animate-spin rounded-full border-2 border-t-transparent mr-1"></div>
              Loading...
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {/* Refresh button */}
          <Button
            variant="flat"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            className="h-9 w-9 p-0 ml-2 rounded-xl bg-gray-100/50 dark:bg-dark-800/50 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-all"
          >
            <ArrowPathIcon
              className={`h-4.5 w-4.5 text-gray-600 dark:text-dark-300 ${loading ? "animate-spin text-primary-500" : ""}`}
            />
          </Button>

          {activities.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Loaded: {activities.length}
            </span>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {typeof error === "string" ? error : error?.message || "Failed to load timeline activities"}
        </div>
      )}

      {/* Scrollable Content */}
      <div
        id={timelineScrollElementId}
        ref={scrollRef}
        className={`flex-1 min-h-0 overflow-x-hidden custom-scrollbar px-6 py-4 ${!scrollTargetId ? 'overflow-y-auto' : ''}`}
      >
        {activities.length > 0 ? (
          <InfiniteScroll
            dataLength={activities.length}
            next={handleLoadMore}
            hasMore={hasMore && !loading}
            loader={loading ? (
              <div className="flex justify-center py-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="border-primary-500 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"></div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div className="font-medium">Loading more activities...</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Please wait while we fetch more data
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            endMessage={
              <div className="py-6 text-center">
                <div className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-2 text-gray-600 shadow-sm dark:bg-gray-800 dark:text-gray-300">
                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">
                    No more activities to load
                  </span>
                </div>
              </div>
            }
            scrollableTarget={scrollTargetId || timelineScrollElementId}
            className="min-h-full"
          >
            <Timeline pointSize="1.5rem">
              {activities.map((activity, index) => {
                const {
                  icon: IconComponent,
                  color,
                  pointClass,
                } = getActivityStyle(activity.ActivityType, activity.Details);
                const { main: rawMain, remarks } = formatActivityDetails(
                  activity.Details,
                );

                // If activity has an explicit activity name (from API or props) and main is generic status change
                const activityName =
                  activity.ActivityName ||
                  activity.activityName ||
                  activity.ActivityTitle ||
                  activity.activityTitle ||
                  activity.Title ||
                  activity.title ||
                  activity.Name ||
                  activity.name ||
                  null;

                let main = rawMain;
                if (activityName) {
                  if (/^activity status changed/i.test(main)) {
                    main = main.replace(/^activity status changed/i, `Activity: ${activityName} status changed`);
                  } else if (/^activity completed/i.test(main)) {
                    main = main.replace(/^activity completed/i, `Activity: ${activityName} completed`);
                  } else if (
                    !/^activity:/i.test(main) &&
                    (activity.ActivityType?.toUpperCase() === "FOLLOWUP_COMPLETED" ||
                      activity.ActivityType?.toUpperCase() === "FOLLOWUP_UPDATED")
                  ) {
                    main = `Activity: ${activityName} - ${main}`;
                  }
                }
                const stageTag = getStageTag(
                  activity.Details,
                  activity.ActivityType,
                );

                const parsedDetails = parseDiffDetails(activity.Details);
                const diffs = parsedDetails?.fields || null;
                const diffSubject = parsedDetails?.subject || null;
                const parsedClientNote = parseClientNoteDetails(activity.Details, activity.ActivityType);
                const taskMatch = !diffSubject && activity.Details?.match(/for task ['"]([^'"]+)['"]/i);
                const itemSubject = diffSubject || (taskMatch ? taskMatch[1] : null);

                return (
                  <TimelineItem
                    key={activity.ActivityId || `${activity.ActivityType || 'activity'}-${index}`}
                    title={itemSubject ? `${getActivityTitle(activity)}: ${itemSubject}` : getActivityTitle(activity)}
                    time={activity.ActivityDate}
                    point={
                      <div
                        className={`timeline-item-point ${pointClass} text-this dark:text-this-light relative flex shrink-0 items-center justify-center rounded-xl border-2 border-current shadow-lg shadow-current/10 bg-white dark:bg-dark-800`}
                      >
                        <IconComponent className="text-[10px] stroke-[2.5px]" />
                      </div>
                    }
                  >
                    {parsedClientNote ? (
                      <ClientNoteDetailsView
                        parsedClientNote={parsedClientNote}
                        defaultRemarks={remarks}
                      />
                    ) : diffs ? (
                      <div className="mt-2 space-y-2 rounded-xl border border-gray-200/70 bg-gray-50/60 p-3 text-xs dark:border-dark-700/70 dark:bg-dark-800/60">
                        <span className="font-semibold text-gray-600 dark:text-dark-300 block mb-1.5 text-[11px] uppercase tracking-wide">
                          {diffs.length} field{diffs.length > 1 ? "s" : ""} updated:
                        </span>
                        <div className="space-y-2">
                          {diffs.map((d, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-bold text-gray-700 dark:text-dark-200 min-w-[100px]">
                                {d.field}:
                              </span>
                              {d.old ? (
                                <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-600 line-through dark:bg-rose-955/30 dark:text-rose-400 border border-rose-200/50 dark:border-rose-900/30 font-medium">
                                  {d.old}
                                </span>
                              ) : (
                                <span className="italic text-gray-400 dark:text-dark-500">(empty)</span>
                              )}
                              <span className="font-bold text-gray-400">→</span>
                              {d.new ? (
                                <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 dark:bg-emerald-955/30 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/30">
                                  {d.new}
                                </span>
                              ) : (
                                <span className="italic text-gray-400 dark:text-dark-500">(cleared)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/<[a-z][\s\S]*>/i.test(main) ? (
                          <FormattedHtmlContent
                            content={main}
                            className="text-xs text-gray-800 dark:text-dark-100"
                          />
                        ) : (
                          <p className="break-words whitespace-pre-wrap text-xs text-gray-700 dark:text-dark-200">
                            {main}
                          </p>
                        )}
                        {remarks && (
                          <p className="mt-1 text-xs text-gray-600 italic dark:text-gray-400 break-words whitespace-pre-wrap">
                            Note: {remarks}
                          </p>
                        )}
                      </>
                    )}

                    {stageTag && (
                      <div className="mt-3 flex flex-wrap gap-1.5 w-full">
                        <Tag
                          variant="soft"
                          color={stageTag.color}
                          className="border-this-darker/40 dark:border-this-lighter/30 rounded-xl border whitespace-normal break-words h-auto py-1 px-3 text-left leading-normal max-w-full"
                        >
                          {stageTag.label}
                        </Tag>
                      </div>
                    )}

                    <div className="mt-3 space-y-2">
                      {(activity.DoneByName || activity.DoneBy) && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <UserIcon className="h-3 w-3" />
                          <span>By {activity.DoneByName || activity.DoneBy}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <CalendarDaysIcon className="h-3 w-3" />
                        <span>
                          {formatDisplayDate(activity.ActivityDate)}
                        </span>
                      </div>
                    </div>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </InfiniteScroll>
        ) : (
          !loading && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                  No Activities
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No activities found for this lead yet.
                </p>
              </div>
            </div>
          )
        )}
      </div>
    </Card>
  );
}

export default LeadTimeline;
